import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps
} from "components/AsyncAutocompleteWithQuery";
import { useEffect, useRef, useState } from "react";
import { PaginatedResult } from "types/common-types";

type SelectEntityProps<T> = {
  value?: string | null;
  onChange: (id: string | null) => void;
  labelField: keyof T;
  initialValue?: any & { data: any };
  failMessage: string;
  fetchData: (
    searchTerm: string
  ) => Promise<{ result: PaginatedResult<T>; forSearchTerm: string }>;
} & Omit<
  AsyncAutocompleteWithQueryProps<T>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectEntityAsync = <T extends { id?: string }>({
  onChange,
  fetchData,
  initialValue,
  value,
  ...rest
}: SelectEntityProps<T>) => {
  const [entity, setEntity] = useState<T | null>(initialValue?.data ?? null);
  const initialApplied = useRef<boolean>(!!initialValue?.data);

  useEffect(() => {
    // Apply initialValue once when it first becomes available (handles async loads).
    // Skip after the user has interacted to avoid clobbering selection on re-renders.
    if (initialValue?.data && !initialApplied.current) {
      setEntity(initialValue.data);
      initialApplied.current = true;
    }
  }, [initialValue]);

  const onChangeEntity = (e: T | null) => {
    setEntity(e);
    onChange(e?.id ?? null);
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={entity}
      fetchData={fetchData}
      onChange={onChangeEntity}
      idField="id"
    />
  );
};

export default SelectEntityAsync;
